import { useEffect, useRef, useState } from "react";

/** Calls `onVisible` when the sentinel scrolls into view (infinite lists). */
export function useInView<T extends Element>(onVisible: () => void, enabled = true, rootMargin = "600px") {
  const ref = useRef<T>(null);
  const cb = useRef(onVisible);
  cb.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && cb.current(), { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, rootMargin]);
  return ref;
}

export function useDebounced<T>(value: T, delay = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Per-browser stored value (falls back silently when storage is unavailable). */
export function useLocalList(key: string, max = 8): [string[], (v: string) => void, () => void] {
  const [items, setItems] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? "[]") as string[];
    } catch {
      return [];
    }
  });
  const save = (next: string[]) => {
    setItems(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
  };
  return [items, (v) => save([v, ...items.filter((i) => i !== v)].slice(0, max)), () => save([])];
}
